import React from "react";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { MaterialTooltip } from "src/app/components/MaterialTooltip/MaterialTooltip";
import METHODS from "src/app/pages/CreatorDiscoveryTool/legacy/app/helpers/methods";
import AudienceFilterLabel from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/AudienceFilterLabel/AudienceFilterLabel";
import { FILTER_FIELD_NAMES, FILTER_TYPES } from "./utils";

export const createArrayFilterLabel = (
  tag,
  subTag,
  idx,
  removeFilter,
  toggleFilter,
  interests,
  socialProviders,
  isActive = true,
) => {
  const {
    name,
    id,
    defaultMessage,
    field,
    secondField: arraySecondField,
  } = tag;

  let content;
  if (subTag[field]) {
    content = subTag[field];
  } else if (name === FILTER_FIELD_NAMES.CHOSEN_TOPICS) {
    const interestObj = METHODS.findObjBySubTag(subTag, interests);
    content = (
      <IDHFormattedMessage
        id={interestObj?.name || subTag}
        defaultMessage={interestObj?.value || subTag}
      />
    );
  } else if (name === FILTER_FIELD_NAMES.SELECTED_PROVIDERS) {
    const foundSocialProvider = socialProviders.find(
      (provider) => provider.value === subTag,
    );
    if (foundSocialProvider) {
      content = foundSocialProvider.label;
    } else {
      content = subTag;
    }
  } else {
    content = subTag;
  }

  return (
    <AudienceFilterLabel
      key={subTag[field] ? subTag[field] : subTag}
      id={id}
      defaultMessage={defaultMessage}
      removeFilter={
        removeFilter
          ? () => removeFilter(FILTER_TYPES.ARRAY, { ...tag, idx })
          : null
      }
      toggleFilter={() => toggleFilter(FILTER_TYPES.ARRAY, { ...tag, idx })}
      isActive={isActive}
    >
      {content}
      {subTag[arraySecondField] && (
        <>
          &nbsp;&nbsp;
          {subTag[arraySecondField]}
          &nbsp;%
        </>
      )}
    </AudienceFilterLabel>
  );
};

export const createInfluencerFollowersLabel = (
  tag,
  subTag,
  idx,
  removeFilter,
  toggleFilter,
  isActive = true,
) => {
  const { id, defaultMessage } = tag;
  let rangeText;

  if (subTag.min && subTag.max) {
    rangeText = `${METHODS.splitNumbers(subTag.min)} - ${METHODS.splitNumbers(subTag.max)}`;
  } else {
    const minText = subTag.min !== null ? METHODS.splitNumbers(subTag.min) : "";
    const maxText = subTag.max !== null ? METHODS.splitNumbers(subTag.max) : "";
    rangeText = `${minText} > ${maxText}`;
  }

  return (
    <AudienceFilterLabel
      id={id}
      key={`${id}_${idx}`}
      defaultMessage={defaultMessage}
      removeFilter={
        removeFilter
          ? () => removeFilter(FILTER_TYPES.ARRAY, { ...tag, idx })
          : null
      }
      toggleFilter={() => toggleFilter(FILTER_TYPES.ARRAY, { ...tag, idx })}
      isActive={isActive}
    >
      {rangeText}
    </AudienceFilterLabel>
  );
};

export const createSubfieldsLabel = (
  tag,
  index,
  searchFilters,
  genders,
  removeFilter,
  toggleFilter,
  isActive = true,
) => {
  const { name, id, defaultMessage, subfields, dash } = tag;

  let firstFieldContent;
  if (name === FILTER_FIELD_NAMES.AUDIENCE_GENDER) {
    firstFieldContent = genders.filter(
      (item) => item.value === searchFilters[subfields[0]].value,
    )[0].label;
  } else if (name === FILTER_FIELD_NAMES.INFLUENCER_AGE) {
    firstFieldContent = searchFilters[subfields[0]]?.value;
  } else {
    firstFieldContent = searchFilters[subfields[0]]?.label;
  }

  let separator;
  if (!dash) {
    separator = " ";
  } else {
    const hasFirstField = searchFilters[subfields[0]];
    const hasSecondField = searchFilters[subfields[1]];

    if (
      (hasFirstField && !hasSecondField) ||
      (!hasFirstField && hasSecondField)
    ) {
      separator = "<";
    } else {
      separator = "-";
    }
  }

  let secondFieldContent;
  if (name === FILTER_FIELD_NAMES.AUDIENCE_GENDER) {
    secondFieldContent = `${searchFilters[subfields[1]]?.value} %`;
  } else if (name === FILTER_FIELD_NAMES.INFLUENCER_AGE) {
    secondFieldContent = searchFilters[subfields[1]]?.value;
  } else {
    secondFieldContent = searchFilters[subfields[1]]?.label;
  }

  return (
    <AudienceFilterLabel
      key={`${id}_${index}`}
      id={id}
      defaultMessage={defaultMessage}
      removeFilter={
        removeFilter
          ? () => removeFilter(FILTER_TYPES.MANY_FIELDS, { ...tag })
          : null
      }
      toggleFilter={() => toggleFilter(FILTER_TYPES.MANY_FIELDS, { ...tag })}
      isActive={isActive}
    >
      {firstFieldContent}
      {separator}
      {secondFieldContent}
      {searchFilters[subfields[2]] && (
        <>&nbsp; &nbsp; {searchFilters[subfields[2]]?.label}</>
      )}
    </AudienceFilterLabel>
  );
};

export const createSocialProviderLabel = (
  tag,
  searchFilters,
  socialProviders,
  removeFilter,
  toggleFilter,
  isActive = true,
) => {
  const { name, id, defaultMessage, field } = tag;
  const providerData = socialProviders.find(
    (item) => item.value === searchFilters[name].value,
  );

  if (!providerData) {
    return null;
  }

  return (
    <MaterialTooltip
      right
      content={
        <IDHFormattedMessage
          id="front_you_can_select_only_one_platform"
          defaultMessage="You can select only one platform"
        />
      }
    >
      <AudienceFilterLabel
        key={id}
        id={id}
        disabled
        defaultMessage={defaultMessage}
        removeFilter={
          removeFilter
            ? () => removeFilter(FILTER_TYPES.SINGLE_FIELD, { ...tag })
            : null
        }
        toggleFilter={() => toggleFilter(FILTER_TYPES.SINGLE_FIELD, { ...tag })}
        isActive={isActive}
      >
        {providerData[field]}
      </AudienceFilterLabel>
    </MaterialTooltip>
  );
};

export const createSingleFieldLabel = (
  tag,
  index,
  searchFilters,
  genders,
  removeFilter,
  toggleFilter,
  isActive = true,
) => {
  const { name, id, defaultMessage, field } = tag;

  let content = null;
  if (typeof searchFilters[name] === "boolean") {
    content = null;
  } else if (name === FILTER_FIELD_NAMES.INFLUENCER_GENDER) {
    const foundGender = genders.find(
      (item) => item.value === searchFilters[name].value,
    );
    content = foundGender ? foundGender[field] : null;
  } else if (name === FILTER_FIELD_NAMES.INFLUENCER_SOCIAL_USERNAME) {
    content = searchFilters[name];
  } else if (name === FILTER_FIELD_NAMES.CREATORS_AVAILABLE_IN_CRM) {
    if (searchFilters[name] === "include") {
      content = (
        <IDHFormattedMessage
          id="front_available_in_crm"
          defaultMessage="Available in CRM"
        />
      );
    } else if (searchFilters[name] === "exclude") {
      content = (
        <IDHFormattedMessage
          id="front_not_available_in_crm"
          defaultMessage="Not available in CRM"
        />
      );
    } else {
      content = null;
    }
  } else {
    content = searchFilters[name][field];
  }

  return (
    <AudienceFilterLabel
      key={`${id}_${-index}`}
      id={id}
      disabled={name === FILTER_FIELD_NAMES.INFLUENCER_SOCIAL_PROVIDER}
      name={name}
      defaultMessage={defaultMessage}
      removeFilter={
        removeFilter
          ? () => removeFilter(FILTER_TYPES.SINGLE_FIELD, { ...tag })
          : null
      }
      toggleFilter={() => toggleFilter(FILTER_TYPES.SINGLE_FIELD, { ...tag })}
      isActive={isActive}
    >
      {content}
    </AudienceFilterLabel>
  );
};
